import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Crown, Rocket, HeartHandshake, Check } from "lucide-react";
import PayPalCheckoutButton from "../components/PayPalCheckoutButton";

export default function Pricing() {
  const { user, refresh } = useAuth();
  const [loading, setLoading] = useState("");
  const navigate = useNavigate();

  const buyPro = async () => {
    if (!user) { toast.error("Log in to upgrade to Pro."); return; }
    setLoading("pro");
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "pro_lifetime",
        origin_url: window.location.origin,
        user_id: user.id,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setLoading("");
    }
  };

  const onProPaid = async () => {
    if (typeof refresh === "function") { try { await refresh(); } catch (_) { /* ignore */ } }
    navigate("/payment/success?session_id=paypal");
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-12" data-testid="pricing-page">
      <div className="text-center mb-12">
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#E63946] mb-3">Pricing</div>
        <h1 className="font-serif font-black text-5xl md:text-6xl tracking-tighter">
          Support the site.<br /><span className="italic font-normal">Support the community.</span>
        </h1>
        <p className="text-black/60 mt-4 max-w-xl mx-auto">
          Three ways to level up your DealHub experience. Sandbox mode — use test card <span className="font-mono">4242 4242 4242 4242</span>.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* PRO */}
        <div className={`border-2 ${user?.is_pro ? "border-emerald-600" : "border-black"} bg-white p-8 flex flex-col`} data-testid="plan-pro">
          <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.25em] text-[#E63946] mb-4">
            <Crown className="w-3.5 h-3.5" strokeWidth={2} /> Most popular
          </div>
          <div className="font-serif font-black text-3xl leading-tight tracking-tighter">DealHub Pro</div>
          <div className="mt-2 flex items-baseline gap-1">
            <span className="font-serif font-black text-4xl">$9.99</span>
            <span className="text-xs font-mono uppercase tracking-widest text-black/50">lifetime</span>
          </div>
          <ul className="mt-6 space-y-2 text-sm text-black/70 flex-1">
            <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" strokeWidth={2} />Daily digest 1 hour earlier</li>
            <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" strokeWidth={2} />PRO byline on every submission</li>
            <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" strokeWidth={2} />Priority admin review queue</li>
            <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" strokeWidth={2} />Ad-free forever</li>
          </ul>
          {user?.is_pro ? (
            <div className="mt-6 text-center bg-emerald-100 text-emerald-800 px-4 py-3 font-bold uppercase tracking-widest text-xs" data-testid="pro-owned">
              You&rsquo;re Pro ✓
            </div>
          ) : (
            <>
              <button
                onClick={buyPro}
                disabled={loading === "pro"}
                className="mt-6 bg-black text-white px-5 py-3 font-bold uppercase tracking-widest text-xs hover:bg-[#E63946] disabled:opacity-50"
                data-testid="buy-pro"
              >
                {loading === "pro" ? "Redirecting…" : "Go Pro — $9.99 (Card)"}
              </button>
              {user && (
                <>
                  <div className="mt-3 flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-black/35">
                    <div className="flex-1 h-px bg-black/10" />
                    <span>Or</span>
                    <div className="flex-1 h-px bg-black/10" />
                  </div>
                  <PayPalCheckoutButton
                    payload={{ lookup_key: "pro_lifetime", origin_url: window.location.origin }}
                    onPaid={onProPaid}
                    testIdPrefix="paypal-pro"
                  />
                </>
              )}
            </>
          )}
        </div>

        {/* BOOST */}
        <div className="border-2 border-black/15 bg-white p-8 flex flex-col" data-testid="plan-boost">
          <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.25em] text-[#E63946] mb-4">
            <Rocket className="w-3.5 h-3.5" strokeWidth={2} /> For submitters
          </div>
          <div className="font-serif font-black text-3xl leading-tight tracking-tighter">Boost a Submission</div>
          <div className="mt-2 flex items-baseline gap-1">
            <span className="font-serif font-black text-4xl">$2.99</span>
            <span className="text-xs font-mono uppercase tracking-widest text-black/50">per boost</span>
          </div>
          <ul className="mt-6 space-y-2 text-sm text-black/70 flex-1">
            <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" strokeWidth={2} />Jumps to top of admin queue</li>
            <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" strokeWidth={2} />Auto-featured on approval</li>
            <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" strokeWidth={2} />Highlighted &ldquo;Boosted&rdquo; badge</li>
          </ul>
          <Link
            to="/submit"
            className="mt-6 border border-black text-center px-5 py-3 font-bold uppercase tracking-widest text-xs hover:bg-black hover:text-white"
            data-testid="go-submit"
          >
            Submit a deal &rarr;
          </Link>
        </div>

        {/* TIP */}
        <div className="border-2 border-black/15 bg-white p-8 flex flex-col" data-testid="plan-tip">
          <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.25em] text-[#E63946] mb-4">
            <HeartHandshake className="w-3.5 h-3.5" strokeWidth={2} /> For fans
          </div>
          <div className="font-serif font-black text-3xl leading-tight tracking-tighter">Tip a Contributor</div>
          <div className="mt-2 flex items-baseline gap-1">
            <span className="font-serif font-black text-4xl">$1–$20</span>
            <span className="text-xs font-mono uppercase tracking-widest text-black/50">any amount</span>
          </div>
          <ul className="mt-6 space-y-2 text-sm text-black/70 flex-1">
            <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" strokeWidth={2} />Direct thanks to top submitters</li>
            <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" strokeWidth={2} />Boosts their public tip count</li>
            <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" strokeWidth={2} />Every dollar goes to the community</li>
          </ul>
          <Link
            to="/leaderboard"
            className="mt-6 border border-black text-center px-5 py-3 font-bold uppercase tracking-widest text-xs hover:bg-black hover:text-white"
            data-testid="go-board"
          >
            Pick a contributor &rarr;
          </Link>
        </div>
      </div>

      <p className="text-xs text-black/40 font-mono uppercase tracking-widest text-center mt-12">
        Payments powered by Stripe &amp; PayPal · Sandbox mode · Card 4242 4242 4242 4242 with any future date + any CVC
      </p>
    </div>
  );
}
